import React, { useState } from "react";
import {
  AppBar,
  Toolbar,
  Box,
  Typography,
  Button,
  Avatar,
  IconButton,
  Menu,
  MenuItem,
  Drawer,
  Divider,
  ListItemIcon,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import FlightIcon from "@mui/icons-material/Flight";
import PetsIcon from "@mui/icons-material/Pets";
import DriveEtaIcon from "@mui/icons-material/DriveEta";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import LoginIcon from "@mui/icons-material/Login";
import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import LogoutIcon from "@mui/icons-material/Logout";
import { useNavigate } from "react-router-dom";
import { setAgentId, setInsuranceType } from "../../redux/actions";
import { useDispatch, useSelector } from "react-redux";
import authService from "../../services/authService";

import ResponsiveImage from "./ResponsiveImage";
const tiles = [
  {
    image: "/assets/images/travel.png",
    icon: <FlightIcon />,
    title: "Travel Insurance",
    navigateTo: "/travel-landing",
  },
  {
    image: "/assets/images/pet.png",
    icon: <PetsIcon />,
    title: "Pet Insurance",
    navigateTo: "/pet-landing",
  },
  {
    image: "/assets/images/motor.png",
    icon: <DriveEtaIcon />,
    title: "Motor Insurance",
    navigateTo: "/motor-landing",
  },
  {
    image: "/assets/images/life.png",
    icon: <VerifiedUserIcon />,
    title: "Life Insurance",
    navigateTo: "/coming-soon?product=life",
  },
];

const navButtonSx = {
  color: "text.primary",
  display: "inline-flex",
  alignItems: "center",
  gap: 0.5,
  fontSize: "0.95rem",
  fontWeight: 600,
  letterSpacing: 0.3,
  textTransform: "none",
  position: "relative",
  px: 1.5,
  borderRadius: 2,
  transition: "color .2s ease, background-color .2s ease",
  "&:hover": {
    backgroundColor: "rgba(201,167,119,0.10)",
    color: "#a1885e",
  },
  "&::after": {
    content: '""',
    position: "absolute",
    left: "50%",
    bottom: 4,
    transform: "translateX(-50%)",
    width: 0,
    height: 2,
    bgcolor: "#C9A777",
    transition: "width .25s ease",
    borderRadius: 2,
  },
  "&:hover::after": { width: "60%" },
};

const AppBarComponent = ({ isAppBarFixed, isLoggedIn, isXsScreen }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const isMenuOpen = Boolean(anchorEl);
  const [avatarAnchorEl, setAvatarAnchorEl] = useState(null);
  const isAvatarMenuOpen = Boolean(avatarAnchorEl);
  const [isInsuranceSubMenuOpen, setInsuranceSubMenuOpen] = useState(false);

  const agentName =
    useSelector((/** @type {import("../../redux/types").RootState} */ state) => state.agent?.agentName) ||
    sessionStorage.getItem("agent_name") ||
    "";

  const getInitials = (name) => {
    if (!name) return "U";
    const words = name.trim().split(" ");
    if (words.length === 1) return words[0][0].toUpperCase();
    return (words[0][0] + words[words.length - 1][0]).toUpperCase();
  };

  const agentProfilePic = sessionStorage.getItem("agent_profile_pic");
  const profilePicSrc = agentProfilePic
    ? `data:image/png;base64,${agentProfilePic}`
    : undefined;

  const handleMenuItemClick = (path, insuranceType) => {
    dispatch(setInsuranceType(insuranceType));
    navigate(path);
  };

  const toggleDrawer = (open) => (event) => {
    if (event.type === "keydown" && (event.key === "Tab" || event.key === "Shift")) {
      return;
    }
    setDrawerOpen(open);
  };

  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);
  const handleAvatarMenuOpen = (event) => setAvatarAnchorEl(event.currentTarget);
  const handleAvatarMenuClose = () => setAvatarAnchorEl(null);

  const handleAvatarMenuItemClick = async (path) => {
    handleAvatarMenuClose();
    if (path === "/logout") {
      await authService.logout();
      dispatch(setAgentId(null));
      sessionStorage.removeItem("agent_profile_pic");
      sessionStorage.removeItem("agent_name");
      sessionStorage.removeItem("agent_type");
    }
    navigate(path);
  };

  const scrollToFooter = () => {
    const currentPath = window.location.pathname;
    const isMainPage = currentPath === "/" || currentPath === "/dashboard";

    if (!isMainPage) {
      navigate("/");
      setTimeout(() => {
        window.scrollTo({
          top: document.documentElement.scrollHeight,
          behavior: "smooth",
        });
        setDrawerOpen(false);
      }, 100);
    } else {
      window.scrollTo({
        top: document.documentElement.scrollHeight,
        behavior: "smooth",
      });
      setDrawerOpen(false);
    }
  };

  const handleDrawerMenuItemClick = (path, insuranceType) => {
    dispatch(setInsuranceType(insuranceType));
    setDrawerOpen(false);
    navigate(path);
  };

  const toggleInsuranceSubMenu = () => {
    setInsuranceSubMenuOpen(!isInsuranceSubMenuOpen);
  };

  return (
    <AppBar
      position={isAppBarFixed ? "fixed" : "absolute"}
      elevation={0}
      sx={{
        top: isAppBarFixed ? 0 : 20,
        left: isAppBarFixed ? 0 : "5vw",
        right: isAppBarFixed ? 0 : "5vw",
        width: isAppBarFixed ? "100vw" : "90vw",
        background: isAppBarFixed
          ? "rgba(251, 241, 226, 0.85)"
          : "rgba(251, 241, 226, 0.92)",
        backdropFilter: "saturate(180%) blur(14px)",
        WebkitBackdropFilter: "saturate(180%) blur(14px)",
        borderBottom: isAppBarFixed ? "1px solid rgba(123,110,102,0.10)" : "none",
        boxShadow: isAppBarFixed
          ? "0 8px 24px rgba(123,110,102,0.08)"
          : "0 12px 32px rgba(123,110,102,0.14)",
        borderRadius: isAppBarFixed ? 0 : "999px",
        padding: isXsScreen ? "4px 8px" : "6px 14px",
        transition:
          "top .25s ease, left .25s ease, right .25s ease, width .25s ease, border-radius .25s ease, box-shadow .25s ease, background .25s ease",
      }}
    >
      <Toolbar sx={{ display: "flex", justifyContent: "space-between" }}>
        <Box
          display="flex"
          alignItems="center"
          sx={{
            cursor: "pointer",
            transition: "transform .25s ease",
            "&:hover": { transform: "scale(1.02)" },
          }}
          onClick={() => navigate("/")}
        >
          <ResponsiveImage
            src="/assets/images/logo-with-name.png"
            alt="SmartSure"
            sizes={isXsScreen ? "167px" : "214px"}
            loading="eager"
            sx={{
              height: isXsScreen ? 56 : 72,
              width: "auto",
              mr: "10px",
              ml: isXsScreen ? 0 : "10px",
            }}
          />
        </Box>

        <Box
          sx={{
            display: { xs: "none", md: "flex" },
            alignItems: "center",
            gap: 1.5,
          }}
        >
          <Button color="inherit" sx={navButtonSx} onClick={handleMenuOpen}>
            Insurance <ExpandMoreIcon sx={{ fontSize: 20 }} />
          </Button>
          <Menu
            anchorEl={anchorEl}
            open={isMenuOpen}
            onClose={handleMenuClose}
            MenuListProps={{
              sx: {
                py: 1,
                minWidth: 260,
                bgcolor: "#fff",
              },
            }}
            TransitionProps={{ timeout: 180 }}
            sx={{
              "& .MuiPaper-root": {
                mt: 1,
                borderRadius: 3,
                border: "1px solid rgba(123,110,102,0.10)",
                boxShadow: "0 18px 40px rgba(123,110,102,0.18)",
                overflow: "hidden",
              },
            }}
          >
            {tiles.map((tile, index) => (
              <MenuItem
                key={index}
                onClick={() => handleMenuItemClick(tile.navigateTo, tile.title)}
                sx={{
                  py: 1.5,
                  px: 2.25,
                  gap: 1.5,
                  transition: "all .2s ease",
                  borderRadius: 2,
                  mx: 1,
                  "&:hover": {
                    bgcolor: "#fdf6ea",
                    transform: "translateX(2px)",
                  },
                }}
              >
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: "50%",
                    bgcolor: "#fdf6ea",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#a1885e",
                    flexShrink: 0,
                  }}
                >
                  {React.cloneElement(tile.icon, { sx: { fontSize: 20 } })}
                </Box>
                <Typography
                  sx={{
                    fontSize: "0.95rem",
                    fontWeight: 600,
                    color: "#3d322b",
                  }}
                >
                  {tile.title}
                </Typography>
              </MenuItem>
            ))}
          </Menu>
          <Button color="inherit" sx={navButtonSx} onClick={scrollToFooter}>
            About Us
          </Button>
          <Button color="inherit" sx={navButtonSx} onClick={scrollToFooter}>
            Contact
          </Button>
          {!isLoggedIn && (
            <Button
              variant="contained"
              startIcon={<LoginIcon sx={{ fontSize: 18 }} />}
              sx={{
                background:
                  "linear-gradient(135deg, #d3b48a 0%, #C9A777 50%, #b4966b 100%)",
                color: "#fff",
                fontWeight: 700,
                letterSpacing: 0.5,
                textTransform: "none",
                borderRadius: "999px",
                px: 2.75,
                py: 0.9,
                fontSize: "0.95rem",
                boxShadow: "0 8px 18px rgba(201,167,119,0.35)",
                ":hover": {
                  background:
                    "linear-gradient(135deg, #C9A777 0%, #b4966b 50%, #a1885e 100%)",
                  boxShadow: "0 10px 22px rgba(201,167,119,0.45)",
                },
              }}
              onClick={() => navigate("/login")}
            >
              Login
            </Button>
          )}
          {isLoggedIn && (
            <>
              <Avatar
                alt="User Image"
                src={profilePicSrc}
                sx={{
                  width: 44,
                  height: 44,
                  cursor: "pointer",
                  border: "2px solid #fff",
                  boxShadow: "0 4px 14px rgba(123,110,102,0.18)",
                  transition: "transform .2s ease",
                  "&:hover": { transform: "scale(1.05)" },
                  bgcolor: "#a1885e",
                  fontWeight: 700,
                }}
                onClick={handleAvatarMenuOpen}
              >
                {!profilePicSrc && getInitials(agentName)}
              </Avatar>
              <Menu
                anchorEl={avatarAnchorEl}
                open={isAvatarMenuOpen}
                onClose={handleAvatarMenuClose}
                MenuListProps={{
                  sx: {
                    py: 1,
                    minWidth: 220,
                    bgcolor: "#fff",
                  },
                }}
                TransitionProps={{ timeout: 180 }}
                sx={{
                  "& .MuiPaper-root": {
                    mt: 1,
                    borderRadius: 3,
                    border: "1px solid rgba(123,110,102,0.10)",
                    boxShadow: "0 18px 40px rgba(123,110,102,0.18)",
                    overflow: "hidden",
                  },
                }}
              >
                <Box sx={{ px: 2, py: 1.5 }}>
                  <Typography variant="caption" sx={{ color: "#9c8e85", letterSpacing: 1.2, fontWeight: 700 }}>
                    SIGNED IN
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#3d322b", fontWeight: 600 }}>
                    {agentName || "Agent"}
                  </Typography>
                </Box>
                <Divider sx={{ borderColor: "rgba(123,110,102,0.10)" }} />
                <MenuItem
                  onClick={() => handleAvatarMenuItemClick("/agent-profile")}
                  sx={{ py: 1.25, px: 2, gap: 1.25 }}
                >
                  <ListItemIcon sx={{ minWidth: 0, color: "#a1885e" }}>
                    <AccountCircleOutlinedIcon fontSize="small" />
                  </ListItemIcon>
                  <Typography sx={{ fontSize: "0.95rem", color: "#3d322b", fontWeight: 500 }}>
                    Profile
                  </Typography>
                </MenuItem>
                <MenuItem
                  onClick={() => handleAvatarMenuItemClick("/logout")}
                  sx={{ py: 1.25, px: 2, gap: 1.25 }}
                >
                  <ListItemIcon sx={{ minWidth: 0, color: "#a1885e" }}>
                    <LogoutIcon fontSize="small" />
                  </ListItemIcon>
                  <Typography sx={{ fontSize: "0.95rem", color: "#3d322b", fontWeight: 500 }}>
                    Logout
                  </Typography>
                </MenuItem>
              </Menu>
            </>
          )}
        </Box>
        <Box
          sx={{
            display: { xs: "flex", md: "none" },
            alignItems: "center",
            gap: 1.5,
          }}
        >
          {!isLoggedIn && (
            <Button
              variant="contained"
              sx={{
                background:
                  "linear-gradient(135deg, #d3b48a 0%, #C9A777 50%, #b4966b 100%)",
                color: "#fff",
                fontWeight: 700,
                borderRadius: "999px",
                px: 2.5,
                py: 0.7,
                fontSize: "0.9rem",
                textTransform: "none",
                boxShadow: "0 6px 14px rgba(201,167,119,0.35)",
                ":hover": { boxShadow: "0 8px 18px rgba(201,167,119,0.45)" },
              }}
              onClick={() => navigate("/login")}
            >
              Login
            </Button>
          )}
          {isLoggedIn && (
            <Avatar
              alt="User Image"
              src={profilePicSrc}
              sx={{
                width: 42,
                height: 42,
                cursor: "pointer",
                border: "2px solid #fff",
                boxShadow: "0 4px 14px rgba(123,110,102,0.18)",
                bgcolor: "#a1885e",
                fontWeight: 700,
              }}
              onClick={handleAvatarMenuOpen}
            >
              {!profilePicSrc && getInitials(agentName)}
            </Avatar>
          )}
          <IconButton
            edge="end"
            aria-label="menu"
            onClick={toggleDrawer(true)}
            sx={{
              bgcolor: "#fff",
              border: "1px solid rgba(123,110,102,0.14)",
              borderRadius: 2,
              ":hover": { bgcolor: "#fdf6ea" },
            }}
          >
            <MenuIcon sx={{ color: "#7B6E66", fontSize: "1.6rem" }} />
          </IconButton>
        </Box>
      </Toolbar>
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={toggleDrawer(false)}
        PaperProps={{
          sx: {
            background:
              "linear-gradient(180deg, #FBF1E2 0%, #fdf6ea 60%, #fff 100%)",
            width: { xs: "78vw", sm: "62vw" },
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            p: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2,
            }}
          >
            <ResponsiveImage
              src="/assets/images/logo-with-name.png"
              alt="SmartSure"
              sizes="167px"
              sx={{ height: 56, width: "auto" }}
            />
            <IconButton onClick={toggleDrawer(false)} sx={{ color: "#7B6E66" }}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Divider sx={{ borderColor: "rgba(123,110,102,0.12)", mb: 1 }} />

          <Button
            variant="text"
            sx={{
              textAlign: "left",
              fontSize: "1.05rem",
              color: "#3d322b",
              width: "100%",
              justifyContent: "space-between",
              fontWeight: 700,
              textTransform: "none",
              py: 1.25,
              borderRadius: 2,
              ":hover": { bgcolor: "rgba(201,167,119,0.10)" },
            }}
            onClick={toggleInsuranceSubMenu}
          >
            Insurance
            {isInsuranceSubMenuOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </Button>
          {isInsuranceSubMenuOpen && (
            <Box sx={{ pl: 1.5, mt: 0.5, mb: 1 }}>
              {tiles.map((tile, index) => (
                <Button
                  key={index}
                  variant="text"
                  startIcon={React.cloneElement(tile.icon, { sx: { color: "#a1885e" } })}
                  sx={{
                    textAlign: "left",
                    fontSize: "0.95rem",
                    color: "#7B6E66",
                    width: "100%",
                    justifyContent: "flex-start",
                    fontWeight: 600,
                    textTransform: "none",
                    py: 1,
                    pl: 1.5,
                    borderRadius: 2,
                    ":hover": { bgcolor: "rgba(201,167,119,0.10)", color: "#3d322b" },
                  }}
                  onClick={() => handleDrawerMenuItemClick(tile.navigateTo, tile.title)}
                >
                  {tile.title}
                </Button>
              ))}
            </Box>
          )}
          <Button
            variant="text"
            sx={{
              textAlign: "left",
              fontSize: "1.05rem",
              color: "#3d322b",
              width: "100%",
              justifyContent: "flex-start",
              fontWeight: 700,
              textTransform: "none",
              py: 1.25,
              borderRadius: 2,
              ":hover": { bgcolor: "rgba(201,167,119,0.10)" },
            }}
            onClick={scrollToFooter}
          >
            About Us
          </Button>
          <Button
            variant="text"
            sx={{
              textAlign: "left",
              fontSize: "1.05rem",
              color: "#3d322b",
              width: "100%",
              justifyContent: "flex-start",
              fontWeight: 700,
              textTransform: "none",
              py: 1.25,
              borderRadius: 2,
              ":hover": { bgcolor: "rgba(201,167,119,0.10)" },
            }}
            onClick={scrollToFooter}
          >
            Contact
          </Button>
        </Box>
      </Drawer>
      {isLoggedIn && (
        <Menu
          anchorEl={avatarAnchorEl}
          open={isAvatarMenuOpen && !isXsScreen ? false : isAvatarMenuOpen}
          onClose={handleAvatarMenuClose}
          MenuListProps={{
            sx: { py: 1, minWidth: 220, bgcolor: "#fff" },
          }}
          TransitionProps={{ timeout: 180 }}
          sx={{
            display: { xs: "block", md: "none" },
            "& .MuiPaper-root": {
              mt: 1,
              borderRadius: 3,
              border: "1px solid rgba(123,110,102,0.10)",
              boxShadow: "0 18px 40px rgba(123,110,102,0.18)",
              overflow: "hidden",
            },
          }}
        >
          <Box sx={{ px: 2, py: 1.5 }}>
            <Typography variant="caption" sx={{ color: "#9c8e85", letterSpacing: 1.2, fontWeight: 700 }}>
              SIGNED IN
            </Typography>
            <Typography variant="body2" sx={{ color: "#3d322b", fontWeight: 600 }}>
              {agentName || "Agent"}
            </Typography>
          </Box>
          <Divider sx={{ borderColor: "rgba(123,110,102,0.10)" }} />
          <MenuItem onClick={() => handleAvatarMenuItemClick("/agent-profile")} sx={{ py: 1.25, gap: 1.25 }}>
            <ListItemIcon sx={{ minWidth: 0, color: "#a1885e" }}>
              <AccountCircleOutlinedIcon fontSize="small" />
            </ListItemIcon>
            Profile
          </MenuItem>
          <MenuItem onClick={() => handleAvatarMenuItemClick("/logout")} sx={{ py: 1.25, gap: 1.25 }}>
            <ListItemIcon sx={{ minWidth: 0, color: "#a1885e" }}>
              <LogoutIcon fontSize="small" />
            </ListItemIcon>
            Logout
          </MenuItem>
        </Menu>
      )}
    </AppBar>
  );
};

export default AppBarComponent;
